import { useLocale as useLocaleR42 } from '@/lib/locale';
import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Navigation } from 'lucide-react';

/**
 * LocationCards — Phase 107
 * -------------------------
 * Shared "Where we swim" location cards used by:
 *   • the private Parent Portal (all coaches with locations)
 *   • the public coach website (Pro coaches only)
 *
 * Each card shows the pool/facility name + full address and two direction
 * buttons — Google Maps and Apple Maps — so families can use their map of
 * choice to see how far away it is. Links open in a new tab; on phones the
 * native app takes over automatically.
 */

export const fullAddress = (loc) => {
    const line2 = [loc.city, loc.state].filter(Boolean).join(', ');
    return [loc.address, [line2, loc.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
};

const gmapsUrl = (loc) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress(loc) || loc.name || '')}`;
const amapsUrl = (loc) => `https://maps.apple.com/?q=${encodeURIComponent(loc.name || 'Location')}&address=${encodeURIComponent(fullAddress(loc) || '')}`;

export const LocationCards = ({ locations, title = 'Where we swim', subtitle }) => {
    const { t: t42 } = useLocaleR42();
    const locs = (locations || []).filter((l) => l && (l.address || l.name));
    if (!locs.length) return null;
    return (
        <div data-testid="coach-locations-section">
            <h2 className="font-display text-lg font-bold text-[#0a1628] flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#00d4ff]" /> {title}
            </h2>
            {subtitle && <p className="mt-0.5 text-sm text-[#1a3a5c]/70">{subtitle}</p>}
            <div className={`mt-3 grid gap-3 ${locs.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                {locs.map((loc) => (
                    <Card key={loc.id} className="sd-card p-4" data-testid={`location-card-${loc.id}`}>
                        <div className="font-semibold text-sm text-[#0a1628]" data-testid={`location-name-${loc.id}`}>{loc.name}</div>
                        {fullAddress(loc) && (
                            <div className="mt-0.5 text-xs text-[#1a3a5c]/80" data-testid={`location-address-${loc.id}`}>
                                {fullAddress(loc)}
                            </div>
                        )}
                        {loc.notes && <div className="mt-1 text-[11px] text-[#1a3a5c]/60">{loc.notes}</div>}
                        <div className="mt-2.5 flex flex-wrap gap-2">
                            <Button
                                asChild
                                size="sm"
                                variant="outline"
                                className="h-8 rounded-lg border-[#1a3a5c]/20 px-2.5 text-xs font-semibold text-[#0a1628] hover:border-[#00d4ff]/60 hover:bg-[#e6fbff]/40"
                                data-testid={`location-gmaps-${loc.id}`}
                            >
                                <a href={gmapsUrl(loc)} target="_blank" rel="noopener noreferrer" aria-label={`Directions to ${loc.name} in Google Maps`}>
                                    <Navigation className="mr-1 h-3 w-3" /> {t42("Google Maps")} </a>
                            </Button>
                            <Button
                                asChild
                                size="sm"
                                variant="outline"
                                className="h-8 rounded-lg border-[#1a3a5c]/20 px-2.5 text-xs font-semibold text-[#0a1628] hover:border-[#00d4ff]/60 hover:bg-[#e6fbff]/40"
                                data-testid={`location-amaps-${loc.id}`}
                            >
                                <a href={amapsUrl(loc)} target="_blank" rel="noopener noreferrer" aria-label={`Directions to ${loc.name} in Apple Maps`}>
                                    <Navigation className="mr-1 h-3 w-3" /> {t42("Apple Maps")} </a>
                            </Button>
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default LocationCards;
